import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { TabContext, TabPanel } from "@mui/lab";
import { Box } from "@mui/material";
import BentoTabs from "./bento/BentoTabs";
import { user } from "localStorage";
import { getVisibleTabs } from "utils/pagePermissions";
import { useUserRefreshTick } from "hooks/useUserRefreshTick";

/**
 * tabItems: { text, component, slug?, key?, visible? }[]
 * pageSlug: parent page slug — when given, tabItems are filtered down to
 * the tabs the current user is granted (see utils/pagePermissions.getVisibleTabs).
 * Items without a `slug` (or when pageSlug is omitted) are always shown.
 * visible: optional extra gate for a tab whose access is NOT a tab of pageSlug
 * (e.g. Packages → Change Requests, gated by its own packages/change-requests page).
 * key: optional — `?tab=<key>` in the URL opens that tab first (uncontrolled mode).
 * activeTab/onTabChange: optional — pass both to let the parent control which
 * tab is open (e.g. salesDocuments/index.jsx jumping to Create after a
 * Duplicate action). Omit both (the common case) and the component manages
 * its own tab state as before.
 */
const TabsNavigator = ({ tabItems, pageSlug, activeTab: activeTabProp, onTabChange }) => {
  useUserRefreshTick(); // re-render when the cached profile refreshes — see hooks/useUserRefreshTick.js
  const [searchParams] = useSearchParams();

  const visibleSlugs = pageSlug ? getVisibleTabs(user(), pageSlug).map((t) => t.slug) : null;
  const filteredItems = (visibleSlugs
    ? tabItems.filter((item) => !item.slug || visibleSlugs.includes(item.slug))
    : tabItems
  ).filter((item) => item.visible !== false);

  const [activeTabState, setActiveTabState] = useState(() => {
    const idx = filteredItems.findIndex((item) => item.key && item.key === searchParams.get("tab"));
    return idx >= 0 ? idx : 0;
  });
  const isControlled = activeTabProp !== undefined;
  const activeTab = isControlled ? activeTabProp : activeTabState;
  const setActiveTab = isControlled ? onTabChange : setActiveTabState;

  // Follow later ?tab= changes too (e.g. a notification link while this page
  // is already open — the route doesn't remount).
  const tabParam = searchParams.get("tab");
  useEffect(() => {
    if (isControlled || !tabParam) return;
    const idx = filteredItems.findIndex((item) => item.key && item.key === tabParam);
    if (idx >= 0) setActiveTabState(idx);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- filteredItems is rebuilt every render
  }, [tabParam, isControlled]);

  return (
    <Box sx={{ width: "100%", typography: "body1" }}>
      <TabContext value={activeTab.toString()}>
        <BentoTabs
          tabs={filteredItems.map(({ text }) => ({ label: text }))}
          value={activeTab}
          onChange={setActiveTab}
          sx={{ display: "inline-flex", mb: "20px" }}
        />
        {filteredItems.map(({ component }, index) => {
          return (
            <TabPanel sx={{ p: 0 }} key={index.toString()} value={index.toString()}>
              {component}
            </TabPanel>
          );
        })}
      </TabContext>
    </Box>
  );
};

export default TabsNavigator;
